import React from 'react';
import { Grid, Typography, TextField, MenuItem } from '@mui/material';
import { UtilityType, Supplier } from '../../../../types/utility.types';

interface ContractDetailsSectionProps {
  serviceType?: UtilityType;
  status?: string;
  supplier?: string;
  accountNumber?: string;
  contractStartDate?: Date | null;
  contractEndDate?: Date | null;
  serviceTypes: UtilityType[];
  statuses: string[];
  suppliers: Supplier[];
  onFieldChange: (field: string, value: any) => void;
  isViewMode: boolean;
}

// Format service type in proper case for display
const formatServiceType = (type: UtilityType | string): string => {
  if (!type) return '';
  
  // Handle special cases
  if (type === 'PPA Export Contract') return 'PPA Export Contract';
  if (type === 'EV') return 'EV';
  
  // Convert other types to proper case
  return type.toLowerCase()
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

export const ContractDetailsSection: React.FC<ContractDetailsSectionProps> = ({
  serviceType,
  status,
  supplier,
  accountNumber,
  contractStartDate,
  contractEndDate,
  serviceTypes,
  statuses,
  suppliers,
  onFieldChange,
  isViewMode,
}) => {
  return (
    <Grid item xs={12}>
      <Typography variant="h6" gutterBottom>Contract Details</Typography>
      <Grid container spacing={3}>
        {/* Contract Type */}
        <Grid item xs={12} md={6}>
          <TextField
            select
            fullWidth
            label="Contract Type"
            name="serviceType"
            value={serviceType || ''}
            onChange={(e) => onFieldChange('serviceType', e.target.value)}
            disabled={Boolean(isViewMode)}
            required
          >
            <MenuItem value="">Select Contract Type</MenuItem>
            {serviceTypes.map((type) => (
              <MenuItem key={type} value={type}>
                {formatServiceType(type)}
              </MenuItem>
            ))}
          </TextField>
        </Grid>
        
        {/* Status */}
        <Grid item xs={12} md={6}>
          <TextField
            select
            fullWidth
            label="Status"
            name="status"
            value={status || ''}
            onChange={(e) => onFieldChange('status', e.target.value)}
            disabled={Boolean(isViewMode)}
            required
          >
            <MenuItem value="">Select a status</MenuItem>
            {statuses.map((status) => (
              <MenuItem key={status} value={status}>
                {status}
              </MenuItem>
            ))}
          </TextField>
        </Grid>
        
        {/* Supplier */}
        <Grid item xs={12} md={6}>
          <TextField
            select
            fullWidth
            label="Supplier"
            name="supplier"
            value={supplier || ''}
            onChange={(e) => onFieldChange('supplier', e.target.value)}
            disabled={Boolean(isViewMode)}
            required
          >
            <MenuItem value="">Select a supplier</MenuItem>
            {suppliers.map((supplier) => (
              <MenuItem key={supplier._id} value={supplier._id}>
                {supplier.name}
              </MenuItem>
            ))}
          </TextField>
        </Grid>
        
        {/* Account Number */}
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="Account Number"
            name="accountNumber"
            value={accountNumber || ''}
            onChange={(e) => onFieldChange('accountNumber', e.target.value)}
            disabled={Boolean(isViewMode)}
          />
        </Grid>
        
        {/* Contract Start Date */}
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            type="date"
            label="Contract Start Date"
            name="contractStartDate"
            value={contractStartDate ? contractStartDate.toISOString().split('T')[0] : ''}
            onChange={(e) => onFieldChange('contractStartDate', e.target.value ? new Date(e.target.value) : null)}
            disabled={Boolean(isViewMode)}
            InputLabelProps={{ shrink: true }}
          />
        </Grid>
        
        {/* Contract End Date */}
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            type="date"
            label="Contract End Date"
            name="contractEndDate"
            value={contractEndDate ? contractEndDate.toISOString().split('T')[0] : ''}
            onChange={(e) => onFieldChange('contractEndDate', e.target.value ? new Date(e.target.value) : null)}
            disabled={Boolean(isViewMode)}
            InputLabelProps={{ shrink: true }}
          />
        </Grid>
      </Grid>
    </Grid>
  );
};
